import { Component } from 'react';

import PropTypes from 'prop-types';
import { useLocation } from 'react-router-dom';

import { recordException } from '#client/utils/reporting';

import Empty from '../Empty';
import Error from './Error';

const NETWORK_ERROR_NAMES = ['ChunkLoadError'];

class ErrorBoundaryInternal extends Component {
  constructor(props) {
    super(props);
    this.state = { requestId: null, hasError: false };
  }

  static getDerivedStateFromError(error) {
    // Read/expose request id from graphql if possible
    const {
      graphQLErrors: [
        {
          extensions: {
            exception: {
              requestId
            } = {}
          } = {}
        } = {}
      ] = []
    } = error;

    return { hasError: true, requestId };
  }

  componentDidUpdate(prevProps) {
    const { pathname } = this.props;
    if (prevProps.pathname !== pathname) {
      // Allow setState when someone is trying to navigate away from the error boundry
      // eslint-disable-next-line react/no-did-update-set-state
      this.setState({ requestId: null, hasError: false });
    }
  }

  componentDidCatch(error, errorInfo) {
    const { requestId } = this.state;
    // Try reloading when webpack detects a ChunkLoadError which happens
    // after code releases and during network timeouts
    const hasNetworkError = NETWORK_ERROR_NAMES.includes(error?.name);
    const networkErrorTarget = error?.request;
    const prevNetworkErrorTarget = window.localStorage.getItem('appForceReloaded');
    if (hasNetworkError && prevNetworkErrorTarget !== networkErrorTarget) {
      window.localStorage.setItem('appForceReloaded', networkErrorTarget);
      window.location.reload();
    } else {
      recordException(error, { errorInfo, requestId });
    }
  }

  render() {
    const { children, hideErrorPage } = this.props;
    const { requestId, hasError } = this.state;
    if (hasError && !hideErrorPage) {
      return (
        <Error requestId={requestId} />
      );
    }
    if (hasError && hideErrorPage) {
      return <Empty hideErrorPage />;
    }
    return children;
  }
}

ErrorBoundaryInternal.defaultProps = {
  hideErrorPage: false
};

ErrorBoundaryInternal.propTypes = {
  pathname: PropTypes.string.isRequired,
  children: PropTypes.node.isRequired,
  hideErrorPage: PropTypes.bool
};

// Functional component used for `useLocation` hook.
function ErrorBoundary({ hideErrorPage, children }) {
  const { pathname } = useLocation();
  return (
    <ErrorBoundaryInternal hideErrorPage={hideErrorPage} pathname={pathname}>
      {children}
    </ErrorBoundaryInternal>
  );
}

ErrorBoundary.defaultProps = {
  hideErrorPage: false
};

ErrorBoundary.propTypes = {
  children: PropTypes.node.isRequired,
  hideErrorPage: PropTypes.bool
};

export default ErrorBoundary;
